import { useQuery } from '@tanstack/react-query';
import { useState, useMemo } from 'react';
import type { BookingRequestRead } from '../../../../../js/api/models/BookingRequestRead';
import type { ConnectedFacility } from '../../../../../js/api/models/ConnectedFacility';
import { BookingRequestsService } from '../../../../../js/api/services/BookingRequestsService';
import { BookingRequestDialog } from '../../../../../js/app/components/BookingRequestDialog';
import { BookingRequestStatusBadge } from '../../../../../js/app/components/BookingRequestStatusBadge';
import { useConfirmationModal } from '../../../../../js/app/components/ConfirmationModal';
import { formatApiDate } from '../../../utils/date';

type AssignmentsTabProps = {
  connection: ConnectedFacility;
};

export function AssignmentsTab({ connection }: AssignmentsTabProps) {
  const providerId = connection.provider?.id;
  const facilityId = connection.facility?.id;

  const [selectedAssignmentId, setSelectedAssignmentId] = useState<string | null>(null);
  const [showFullDecline, setShowFullDecline] = useState(false);
  const { showConfirmationModal, ConfirmationModal } = useConfirmationModal();

  const {
    data: assignmentsData,
    isLoading: isLoadingAssignments,
    refetch: refetchAssignments,
  } = useQuery({
    queryKey: ['assignments', providerId, facilityId],
    queryFn: () =>
      BookingRequestsService.bookingRequestsList({
        provider: providerId ? [providerId] : undefined,
        healthcareFacility: facilityId ? [facilityId] : undefined,
      }),
    enabled: !!providerId && !!facilityId,
    select: (data) => data.results || [],
  });

  const assignments = (assignmentsData as BookingRequestRead[]) || [];

  const selectedAssignment = useMemo(() => {
    return assignments.find((assignment) => assignment.id === selectedAssignmentId) || null;
  }, [assignments, selectedAssignmentId]);

  return (
    <div className="tw:space-y-4">
      {/* Assignments */}
      <section>
        {isLoadingAssignments ? (
          <div className="tw:text-sm tw:text-zinc-500 tw:dark:text-zinc-400">
            Loading assignments...
          </div>
        ) : assignments.length > 0 ? (
          <div className="tw:space-y-2">
            {/* eslint-disable-next-line sonarjs/cognitive-complexity */}
            {assignments.map((assignment) => {
              // Get date range from bookings if available
              const sortedBookings = assignment.bookings
                ? [...assignment.bookings].sort((a, b) => {
                    const dateA = a.start_on ? new Date(a.start_on).getTime() : 0;
                    const dateB = b.start_on ? new Date(b.start_on).getTime() : 0;
                    return dateA - dateB;
                  })
                : [];
              const startDate = sortedBookings.length > 0 ? sortedBookings[0]?.start_on : null;
              const endDate =
                sortedBookings.length > 0
                  ? sortedBookings[sortedBookings.length - 1]?.end_on
                  : null;

              return (
                <div
                  key={assignment.id}
                  className="tw:flex tw:items-center tw:justify-between tw:py-1 tw:border-b tw:border-zinc-100 last:tw:border-b-0 tw:dark:border-zinc-700"
                >
                  <div className="tw:flex tw:flex-col tw:gap-1">
                    <button
                      type="button"
                      onClick={() => setSelectedAssignmentId(assignment.id)}
                      className="tw:text-sm tw:font-medium tw:text-left tw:text-brand-green-1 tw:hover:text-brand-green-0 tw:cursor-pointer tw:hover:underline tw:bg-transparent tw:border-0 tw:p-0"
                    >
                      {assignment.code || 'Untitled assignment'}
                    </button>
                    {startDate && (
                      <span className="tw:text-xs tw:text-zinc-500 tw:dark:text-zinc-400">
                        {formatApiDate(startDate, { dateFormat: 'MMM d, yyyy' })}
                        {endDate && endDate !== startDate
                          ? ` - ${formatApiDate(endDate, { dateFormat: 'MMM d, yyyy' })}`
                          : ''}
                      </span>
                    )}
                  </div>
                  <span className="tw:ml-2 tw:inline-flex tw:items-center">
                    <BookingRequestStatusBadge
                      status={assignment.status}
                      bookings={assignment.bookings || []}
                    />
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="tw:text-sm tw:text-zinc-400  tw:italic tw:text-center">
            No assignments found for this connection.
          </div>
        )}
      </section>

      {/* All Connections - Deferred to later phase */}

      {selectedAssignment && (
        <BookingRequestDialog
          bookingRequest={selectedAssignment}
          show={!!selectedAssignment}
          showConfirmationModal={showConfirmationModal}
          onHide={() => setSelectedAssignmentId(null)}
          refetch={() => refetchAssignments()}
          setShowFullDecline={setShowFullDecline}
          showFullDecline={showFullDecline}
        />
      )}
      <ConfirmationModal
        key={showFullDecline ? 'decline' : 'accept'}
        title="Assignment Agreement"
        htmlText={
          showFullDecline ? (
            <>
              <h5 className="text-section-name--dark-brand mb-2">
                {selectedAssignment?.healthcare_facility.name}
              </h5>
              <div className="">
                Are you sure you want to decline <span className="fw-bold text-danger">ALL</span>{' '}
                shifts?
              </div>
              <div className="text-medium text-muted bg-neutral-200 p-2 rounded mt-3">
                If you continue the assignment will be CANCELED. <br /> <br />
                To accept any shift, go back and place check mark next to it and then click the
                Respond to Assignment button.
              </div>
            </>
          ) : (
            <>
              <h5 className="text-section-name--dark-brand mb-2">
                {selectedAssignment?.healthcare_facility.name}
              </h5>
              <div className="">
                By accepting, you <b>acknowledge and agree to</b>
                <ul>
                  <li>dates and times for any shift selected with a checkmark</li>
                  <li>fixed hourly rates noted next to a selected shift</li>
                  <li>
                    Any <b>unchecked</b> shifts will be{' '}
                    <span className="text-danger">rejected</span>
                  </li>
                </ul>
              </div>
              <div className="text-medium text-muted bg-neutral-200 p-2 rounded">
                You also agree to any facility requirements including but not limited to APP
                supervision, chart completion and compliance regulations as outlined in the Provider
                Agreement.
              </div>
            </>
          )
        }
        confirmText={showFullDecline ? 'Decline' : 'Accept'}
        confirmVariant={showFullDecline ? 'danger' : 'primary'}
        cancelText="Go Back"
        cancelVariant="outline-primary"
      />
    </div>
  );
}
